import { ImageIcon } from 'lucide-react';

interface ImagePlaceholderProps {
  /** What screenshot belongs in this frame. */
  label: string;
  /** Optional second line naming what should be visible in the shot. */
  hint?: string;
  /** CSS `aspect-ratio` for the frame. Defaults to a landscape 16/10. */
  ratio?: string;
  /** Extra classes — e.g. a reveal delay wrapper's tone. */
  className?: string;
}

/**
 * A labelled slot where a product screenshot will go.
 *
 * Deliberately loud — dashed outline, mono caption, "image placeholder"
 * stamp — so an unfilled frame is obvious in review rather than reading
 * as a finished empty panel. Swap the whole element for an `<img>` once
 * the asset exists; nothing else on the page depends on it.
 *
 * `role="img"` with a label keeps it announced as one missing image
 * instead of a stack of stray text nodes.
 */
export function ImagePlaceholder({
  label,
  hint,
  ratio = '16 / 10',
  className,
}: ImagePlaceholderProps): React.JSX.Element {
  return (
    <div
      className={`img-ph${className ? ` ${className}` : ''}`}
      style={{ aspectRatio: ratio }}
      role="img"
      aria-label={`Image placeholder — ${label}`}
    >
      <span className="img-ph-stamp">image placeholder</span>
      <ImageIcon size={26} strokeWidth={1.5} aria-hidden="true" />
      <b>{label}</b>
      {hint && <span className="img-ph-hint">{hint}</span>}
    </div>
  );
}
